import _ from "lodash";
import Errors from "../../../utils/Errors";
import {basicContext} from "../../../utils/contextUtils";
import {applyChildrenActionRules} from "../work/actionRules";
import {pushActionStatusMessages} from "../work/followUp";
const {generateFetchFieldListAction} = require('../../../../apps/KpModule/actions/api')
const {setFieldVisibility} = require("../../../../apps/KpModule/actions");
const {getFormValues} = require("../../../../apps/KpModule/selectors");

const OPEN_STAGES = ['target', 'contacted', 'meeting', 'proposal', 'negotiation']

const toObjectID = id => new global.ObjectID(id.toString())
const isSolutionTarget = opportunity => _.get(opportunity, 'target.id') === 'solution'

// Le client d'une opportunité est un groupe (Unit) ou une société (Company), cf. GroupAndCompany
const findClient = async clientId => {
    const _id = toObjectID(clientId)
    return await global.app.K.Company.collection.findOne({_id})
        || await global.app.K.Unit.collection.findOne({_id})
}

// Opportunité gagnée : la solution est déployée chez le client, ou le produit sur mesure est créé
const onWon = async (opportunity, context) => {
    const clientId = toObjectID(opportunity.groupAndCompany.id)

    if (isSolutionTarget(opportunity)) {
        const productId = toObjectID(opportunity.product.id)
        const existing = await global.app.K.Deployment.collection.findOne({product: productId, client: clientId})
        if (existing) return

        await global.app.K.Deployment.save(
            {
                product: {id: opportunity.product.id},
                client: {id: opportunity.groupAndCompany.id},
                contacts: (opportunity.contacts || []).map(contact => ({id: contact.id})),
                status: {id: 'setup'},
                goLiveDate: null,
                endDate: null,
                description: '',
                conversations: []
            },
            {
                ...basicContext(context),
                fieldPath: ['product.id', 'client.id', 'contacts.id', 'status.id', 'goLiveDate', 'endDate', 'description', 'conversations']
            }
        )
        return
    }

    const existing = await global.app.K.Product.collection.findOne({kind: 'custom', client: clientId})
    if (existing) return

    const client = await findClient(clientId)
    await global.app.K.Product.save(
        {
            code: client.code,
            name: opportunity.name,
            kind: {id: 'custom'},
            client: {id: opportunity.groupAndCompany.id},
            owner: opportunity.owner ? {id: opportunity.owner.id} : null,
            description: '',
            state: {id: 'actif'},
            conversations: []
        },
        {
            ...basicContext(context),
            fieldPath: ['code', 'name', 'kind.id', 'client.id', 'owner.id', 'description', 'state.id', 'conversations']
        }
    )
}

export const entity = {
    name: 'Opportunity',
    facets: ['conversations'],
    fields: [
        {path: 'code', unique: true},
        'name',
        'GroupAndCompany',
        {type: 'Contact', link: 'MTM', nullable: true},
        {path: 'target', type: 'OpportunityTarget'},
        {path: 'product', type: 'Product', nullable: true},
        {path: 'stage', type: 'OpportunityStage'},
        {path: 'owner', type: 'User', nullable: true},
        {path: 'team', type: 'User', link: 'MTM', nullable: true},
        {path: 'estimatedAmount', type: 'integer', nullable: true},
        {path: 'expectedCloseDate', type: 'date', nullable: true},
        'description',
        {path: 'actions', type: 'Action', link: 'OTM'},
        {
            path: "sequence", unique: true, ps: {
                object: [{
                    type: "nextSequence",
                    sequenceId: "k.opportunitySeq",
                    formatResult: result => _.padStart(result.toString(), 4, '0')
                }]
            }
        }
    ],
    ps: {
        context: [{
            $$u: function (context, callback) {
                if (this.options.accessType === "S" && context.restAction && context.restAction.crudType === "C") {
                    context.internalFieldPath = [
                        ...new Set([
                            ...context.internalFieldPath,
                            "sequence"
                        ])
                    ]
                }
                callback(null, context)
            }
        }]
    },
    beforeSave: async function(newObject, oldObject, context, callback) {
        if (isSolutionTarget(newObject) && !newObject.product) {
            return callback(new Errors.ValidationError(context.tc('solutionOpportunityRequiresProduct')))
        }
        if (!isSolutionTarget(newObject)) newObject.product = null

        if (_.get(context, 'restAction.crudType') === 'C') {
            newObject.code = `OPP-${newObject.sequence}`
        }

        try {
            // changements de statut des actions, tracés dans leur « Suivi » après l'enregistrement
            context.actionStatusChanges = await applyChildrenActionRules(newObject)
            callback(null, newObject, oldObject)
        } catch (error) {
            callback(error)
        }
    },
    afterSave: async function(newObject, oldObject, context, callback) {
        try {
            const isWon = opportunity => _.get(opportunity, 'stage.id') === 'won'
            await pushActionStatusMessages(context.actionStatusChanges, context)
            if (isWon(newObject) && !isWon(oldObject)) await onWon(newObject, context)
            callback()
        } catch (error) {
            callback(error)
        }
    }
}

export const module_ = {
    object: 'Opportunity',
    category: {
        path: 'work',
        icon: 'check-square'
    },
    defaultSortBy: 'expectedCloseDate',
    defaultSortDirection: 'ASC',
    viewMap: {
        dt: [
            'code',
            {path: 'groupAndCompany', display: 'fullName'},
            'name',
            'target',
            {path: 'product', display: 'fullName'},
            'stage',
            'owner',
            'estimatedAmount',
            'expectedCloseDate'
        ],
        form: {
            fields: [
                {
                    path: 'groupAndCompany',
                    display: 'fullName',
                    placeholder: 'sélectionner le groupe ou la société',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store}) => {
                            store.dispatch(
                                generateFetchFieldListAction(
                                    'm-K-opportunity.Opportunity_contacts',
                                    store.getState,
                                    {data: {groupAndCompany: {id: newValue?.id}}}
                                )
                            )
                        }
                    }
                },
                {path: 'contacts', tKey: 'interlocutor', filters: ['byGroupAndCompany']},
                {path: 'code', disabled: true, required: false},
                {path: 'name', required: true, placeholder: 'renseigner le nom de l’opportunité'},
                {
                    path: 'target',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store}) => {
                            store.dispatch(setFieldVisibility('e_product', _.get(newValue, 'id') === 'solution'))
                        }
                    }
                },
                {path: 'product', display: 'fullName', filters: ['onlySolutions'], placeholder: 'sélectionner la solution proposée'},
                {path: 'stage', default: {id: 'target'}},
                'owner',
                'team',
                {path: 'estimatedAmount', tKey: 'estimatedAmountK€', minValue: 0},
                'expectedCloseDate',
                {path: 'description', tKey: 'synthèse', type: 'richTextEditor'},
                {
                    path: 'actions',
                    defaultSortBy: 'dueDate',
                    defaultSortDirection: 'ASC',
                    viewMap: {
                        dt: ['type', 'name', 'assignee', 'status', 'dueDate'],
                        form: [
                            {path: 'name', required: true},
                            {path: 'type', display: 'fullName'},
                            'assignee',
                            {path: 'status', default: {id: 'todo'}},
                            'dueDate',
                            {path: 'description', type: 'textarea'}
                        ]
                    }
                },
                {path: 'conversations', tKey: 'suivi'}
            ],
            onOpen: ({state, store}) => {
                store.dispatch(setFieldVisibility('e_product', isSolutionTarget(getFormValues(state))))
            }
        }
    },
    filters: [
        {
            path: 'company',
            tKey: 'groupAndCompany',
            object: 'GroupAndCompany',
            display: 'fullName',
            client: true,
            clearable: true,
            default: {id: null},
            query: function(context) {
                const companyId = _.get(context.data, 'company.id')
                return companyId ? {groupAndCompany: toObjectID(companyId)} : {}
            }
        },
        {
            path: 'opportunityStage',
            tKey: 'stage',
            object: 'OpportunityStage',
            display: 'name',
            client: true,
            type: 'tags',
            clearable: true,
            default: OPEN_STAGES.map(id => ({id})),
            query: function(context) {
                const stages = _.get(context.data, 'opportunityStage') || []
                return stages.length ? {stage: {$in: stages.map(o => o.id)}} : {}
            }
        },
        {
            path: 'owner',
            object: 'User',
            display: 'firstname',
            client: true,
            clearable: true,
            placeholder: 'selectUser',
            default: {id: null},
            query: function(context) {
                const userId = _.get(context.data, 'owner.id')
                return userId ? {owner: toObjectID(userId)} : {}
            }
        }
    ]
}
